/**
 * Logout Page - Lenders App
 * Calls logout API and redirects to login page
 */

import { useEffect, useRef, useState } from "react";
import useAppState from "../../common/AppState";
import LogoutApi from "../../api/LogoutApi";
import "./Logout.css";

// Helper to navigate using URL params (no react-router)
const navigateToLogin = (reason?: string) => {
  const params = new URLSearchParams();
  params.set('view', 'login');
  params.set('logout', 'true');
  if (reason) params.set('reason', reason);
  window.history.replaceState({}, "", `?${params.toString()}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

const Logout: React.FC = () => {
  const didLogout = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [logoutReason, setLogoutReason] = useState<string | null>(null);

  const clearProfileData = useAppState((state) => state.clearProfileData);

  // Check if logout was due to timeout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (reason === 'timeout') {
      setLogoutReason('Your session has expired due to inactivity.');
    }
  }, []);

  useEffect(() => {
    if (didLogout.current) return;
    didLogout.current = true;

    if (import.meta.env.DEV) console.log("🔄 Logout: Initiating logout...");

    LogoutApi(
      () => {
        if (import.meta.env.DEV) console.log("✅ Logout: Success");
        clearProfileData();
        navigateToLogin(logoutReason === 'timeout' ? 'timeout' : undefined);
      },
      (data) => {
        if (import.meta.env.DEV) console.log("❌ Logout: Failed", data);
        // Still clear local state even if API fails
        clearProfileData();
        setError("Logout encountered an issue. You have been signed out locally.");
        // Redirect after a short delay
        setTimeout(() => {
          navigateToLogin(logoutReason === 'timeout' ? 'timeout' : undefined);
        }, 2000);
      },
      () => {
        if (import.meta.env.DEV) console.log("❌ Logout: Rejected");
        // Still clear local state even if API fails
        clearProfileData();
        setError("Unable to connect. You have been signed out locally.");
        // Redirect after a short delay
        setTimeout(() => {
          navigateToLogin(logoutReason === 'timeout' ? 'timeout' : undefined);
        }, 2000);
      }
    );
  }, [clearProfileData]);

  return (
    <div className="logout-page">
      <div className="logout-container">
        <div className="logout-card">
          {error || logoutReason ? (
            <>
              <div className="logout-icon logout-icon-warning">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {logoutReason ? (
                    // Clock icon for timeout
                    <>
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </>
                  ) : (
                    // Warning icon for errors
                    <>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </>
                  )}
                </svg>
              </div>
              <h2 className="logout-title">
                {logoutReason ? 'Session Expired' : 'Sign Out Notice'}
              </h2>
              <p className="logout-message">{logoutReason || error}</p>
              <p className="logout-redirect">Redirecting to login...</p>
            </>
          ) : (
            <>
              <div className="logout-icon">
                <svg className="logout-spinner" viewBox="0 0 24 24">
                  <circle
                    className="logout-spinner-track"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                  />
                  <path
                    className="logout-spinner-head"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <h2 className="logout-title">Signing Out</h2>
              <p className="logout-message">Please wait while we securely sign you out...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Logout;

